import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Security,
  Warning,
  Assessment,
  Timeline,
  Speed,
  AttachMoney,
} from "@mui/icons-material";
import {
  // Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import TrendChartMUI from "./charts/TrendChartMUI";
import GaugeChartMUI from "./charts/GaugeChartMUI";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";

// Comparison Card Component
const ComparisonCard = ({
  /* --- visual --- */
  title,
  icon: Icon, // (optional) big icon at the top-right
  tooltipKey, // key into tooltipTexts (e.g. "securityPosture")

  /* --- run-1 / run-2 numbers & labels --- */
  run1Value,
  run1Label,
  run2Value,
  run2Label,

  /* --- improvement line --- */
  improvement, // string, e.g. "+3.2%" or "-1.8%"
  improvementLabel, // "improvement" | "reduction" | custom
}) => {
  /* ---------------------------------------------------------------
     improvement colour & arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }} logic
  --------------------------------------------------------------- */
  const numericVal = parseFloat(improvement.replace(/[+\-%]/g, "")) || 0;
  const isPositive = improvement.startsWith("+");
  const isNegative = improvement.startsWith("-");
  const isZero = numericVal === 0;

  const colour = (() => {
    if (isZero) return "text.secondary";
    if (improvementLabel === "reduction") {
      return isNegative ? "success.main" : "error.main";
    }
    return isPositive ? "success.main" : "error.main";
  })();

  const ArrowIcon = (() => {
    if (isZero) return null;
    if (improvementLabel === "reduction") {
      return isNegative ? TrendingDown : TrendingUp;
    }
    return isPositive ? TrendingUp : TrendingDown;
  })();

  /* --------------------------------------------------------------- */
  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid #5391F7",
        borderRadius: 2,
        position: "relative" /* so the info-icon can be absolute */,
      }}
    >
      {/* ────────── tooltip + info icon ────────── */}
      {tooltipKey && (
        <Tooltip
          title={tooltipTexts[tooltipKey]}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              p: "4px",
              color: "#5391F7",
              "&:hover": { backgroundColor: "rgba(83,145,247,0.1)" },
            }}
            aria-label="info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}

      <CardContent>
        {/* header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography sx={{ fontSize: 23, fontWeight: 400 }}>
            {title}
          </Typography>
          {Icon && <Icon color="action" />}
        </Stack>

        {/* run-1 vs run-2 boxes */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Run 1 */}
          <Grid item xs={6}>
            <Paper
              sx={{
                p: 2,
                bgcolor: "#fef2f2",
                textAlign: "center",
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{ fontSize: 15, fontWeight: 400, color: "#dc2626" }}
              >
                Run 1
              </Typography>
              <Typography
                sx={{ color: "#b91c1c", fontSize: 23, fontWeight: 600 }}
              >
                {run1Value}
              </Typography>
              <Typography sx={{ color: "#dc2626", fontSize: 12 }}>
                {run1Label}
              </Typography>
            </Paper>
          </Grid>

          {/* Run 2 */}
          <Grid item xs={6}>
            <Paper
              sx={{
                p: 2,
                bgcolor: "#f0fdf4",
                textAlign: "center",
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{ fontSize: 15, fontWeight: 400, color: "#16a34a" }}
              >
                Run 2
              </Typography>
              <Typography
                sx={{ color: "#15803d", fontSize: 23, fontWeight: 600 }}
              >
                {run2Value}
              </Typography>
              <Typography sx={{ color: "#16a34a", fontSize: 12 }}>
                {run2Label}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* improvement line */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          {ArrowIcon && <ArrowIcon sx={{ color: colour, fontSize: 20 }} />}
          <Typography
            variant="body2"
            sx={{ color: colour, fontWeight: "bold" }}
          >
            {improvement}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {improvementLabel}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Transform function for Not Fixed Severity Data
const transformNotFixedSeverityData = (severityData) => {
  return Object.entries(severityData).map(([severity, count]) => ({
    name: severity,
    value: count,
  }));
};

// DonutChart component (unchanged)
const DonutChart = ({ data, title, centerText }) => {
  const COLORS = ["#f44336", "#ff5722", "#ff9800", "#4caf50"];

  // Calculate total for center display
  const totalCount = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "#5391F7",
        borderRadius: 2,
        position: "relative",
      }}
    >
      <Tooltip
        title={tooltipTexts["Open Vulnerabilities by Category"]}
        placement="top"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 300,
              fontSize: "14px",
              lineHeight: 1.4,
            },
          },
        }}
      >
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "4px",
            color: "#5391F7",
            zIndex: 1,
            "&:hover": {
              backgroundColor: "rgba(83, 145, 247, 0.1)",
            },
          }}
          aria-label="Info"
        >
          <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
        </IconButton>
      </Tooltip>

      <CardContent>
        <Typography variant="h6" gutterBottom align="center" sx={{ pr: 4 }}>
          {title}
        </Typography>

        <Box sx={{ position: "relative", height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value, name]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Text Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none", // Allows chart interaction through the text
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                // color: "#1976d2",
                lineHeight: 1,
              }}
            >
              {centerText || totalCount}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mt: 2 }}
        >
          {data.map((entry, index) => (
            <Chip
              key={entry.name}
              label={`${entry.name}: ${entry.value}`}
              size="small"
              sx={{
                bgcolor: COLORS[index % COLORS.length],
                color: "white",
                mb: 0.5,
                fontWeight: 500,
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Box>
  );
};

// Transform function
const transformClosedVulnData = (severityData) => {
  const colorMap = {
    Critical: "#f44336",
    High: "#ff5722",
    Medium: "#ff9800",
    Low: "#4caf50",
  };

  return Object.entries(severityData).map(([severity, count]) => ({
    name: severity,
    value: count,
    color: colorMap[severity],
  }));
};

// Transform function
const transformNewVulnerabilitiesData = (severityData) => {
  const colorMap = {
    Critical: "#f44336",
    High: "#ff5722",
    Medium: "#ff9800",
    Low: "#4caf50",
  };

  return Object.entries(severityData).map(([severity, count]) => ({
    severity: severity,
    count: count,
    color: colorMap[severity],
  }));
};

// Transform run1 OWASP metrics data
const transformRun1OwaspData = (owaspMetrics) => {
  const categoryCount = {};

  // Count vulnerabilities by OWASP category
  owaspMetrics.forEach((item) => {
    const category = item.owasp_category;
    categoryCount[category] = (categoryCount[category] || 0) + 1;
  });

  // Color mapping for OWASP categories
  const colorMap = {
    "A01:2021 - Broken Access Control": "#3B82F6",
    "A02:2021 - Cryptographic Failures": "#8B5CF6",
    "A03:2021 - Injection": "#06B6D4",
    "A04:2021 - Insecure Design": "#10B981",
    "A05:2021 - Security Misconfiguration": "#F59E0B",
    "A06:2021 - Vulnerable and Outdated Components": "#DDA0DD",
    "A07:2021 - Identification and Authentication Failures": "#98D8C8",
    "A08:2021 - Software and Data Integrity Failures": "#F7DC6F",
    "A09:2021 - Security Logging and Monitoring Failures": "#BB8FCE",
    "A10:2021 - Server-Side Request Forgery": "#85C1E9",
  };

  // Get all OWASP categories (including those with 0 count)
  const allCategories = Object.keys(colorMap);

  return allCategories.map((category) => ({
    name: category,
    value: categoryCount[category] || 0,
    color: colorMap[category],
  }));
};

// Transform run2 OWASP not fixed vulnerabilities data
const transformRun2OwaspData = (owaspNotFixedVulnerabilities, run1Data) => {
  const categoryCount = {};

  // Count not fixed vulnerabilities by OWASP category
  owaspNotFixedVulnerabilities.forEach((item) => {
    const category = item.owasp_category;
    categoryCount[category] = (categoryCount[category] || 0) + 1;
  });

  // Create run2 data structure matching run1 categories
  return run1Data.map((run1Item) => ({
    name: run1Item.name,
    value: categoryCount[run1Item.name] || 0,
    color: run1Item.color,
  }));
};

const OwaspComparisonChart = ({ run1Data, run2Data, title }) => {
  // Filter out categories that have 0 vulnerabilities in both runs for cleaner display
  const filteredData = run1Data.filter(
    (item, index) => item.value > 0 || run2Data[index].value > 0
  );

  const maxValue = Math.max(
    ...filteredData.map((item) => item.value),
    ...filteredData.map(
      (_, index) =>
        run2Data.find((r2) => r2.name === filteredData[index].name)?.value || 0
    )
  );

  return (
    <Box sx={{ height: "100%" }}>
      <CardContent>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        <Stack spacing={3}>
          {filteredData.map((item, index) => {
            const run2Item = run2Data.find((r2) => r2.name === item.name);
            const reduction =
              item.value > 0
                ? Math.round(((item.value - run2Item.value) / item.value) * 100)
                : 0;

            return (
              <Box key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    {item.name}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption">
                      Run 1: {item.value}
                    </Typography>
                    <Typography variant="caption">
                      Run 2: {run2Item.value}
                    </Typography>
                    {reduction >= 0 ? (
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight="bold"
                      >
                        -{reduction}%
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        color="error.main"
                        fontWeight="bold"
                      >
                        +{Math.abs(reduction)}%
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={maxValue > 0 ? (item.value / maxValue) * 100 : 0}
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: item.color,
                            opacity: 0.6,
                            borderRadius: 3,
                          },
                        }}
                      />
                      {/* FIXED: Show individual item count instead of total */}
                      <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Run 1
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={
                          maxValue > 0 ? (run2Item.value / maxValue) * 100 : 0
                        }
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: item.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      {/* FIXED: Show individual item count instead of total */}
                      <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                        {run2Item.value}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Run 2
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* Summary Section */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Total Run 1: {run1Data.reduce((sum, item) => sum + item.value, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Run 2: {run2Data.reduce((sum, item) => sum + item.value, 0)}
            </Typography>
            <Typography variant="body2" color="success.main" fontWeight="bold">
              Overall Reduction:{" "}
              {Math.round(
                (1 -
                  run2Data.reduce((sum, item) => sum + item.value, 0) /
                    run1Data.reduce((sum, item) => sum + item.value, 0)) *
                  100
              )}
              %
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Box>
  );
};

const tooltipTexts = {
  "Overall Security Posture":
    "Shows the variation in overall security posture across the two runs. A lower value in Run 2 indicates stronger application security and reduced exposure.",
  "Business Impact Change":
    "Compares how business risk has evolved between the two runs. A lower or reduced percentage shows improved operational safety.",
  "Technical Impact Change":
    "Reflects change in technical risk (e.g., data loss, system outage) between runs. A reduction indicates better resilience.",
  "Vulnerability Count Change":
    "Shows the net increase or decrease in true positive vulnerabilities. A drop means improved security.",
  "ROI Change":
    "Highlights the change in cost-effectiveness of testing. A lower ROI in Run 2 suggests greater security value achieved for less investment.",
  "Risk Rating Change":
    "Compares severity levels of vulnerabilities across the two runs. A decrease shows fewer critical/high-risk findings.",
  "Risk Rating Weighted Score Change":
    "Represents the change in the cumulative risk rating of the application. A lower score in Run 2 indicates reduced overall security risk.",
  "Remediation Effectiveness":
    "Shows the percentage of Run 1 vulnerabilities that have been resolved in Run 2. Higher values reflect strong fix coverage.",
  "Risk Exposure Reduction":
    "Measures how much the total risk exposure has dropped between runs. A higher reduction means stronger security improvement.",
  "Attack Surface Reduction":
    "Indicates how much the potential entry points have been reduced in Run 2 compared to Run 1. A higher drop shows improvement.",
  "Age of Vulnerabilities Resolved":
    "Shows how long the resolved vulnerabilities were open before being fixed. Shorter durations indicate better remediation discipline.",
  "Average Time to Remediation":
    "Compares average fix time between runs. A decrease suggests faster response cycles.",
  "New Vulnerabilities in Run 2":
    "Highlights vulnerabilities introduced after Run 1. Helps identify new risks or regressions.",
  "Open Vulnerabilities by Category":
    "Displays unresolved issues from both runs grouped by vulnerability type. Aids in identifying persistent problem areas.",
  "Closed Vulnerabilities by Severity":
    "Compares how many vulnerabilities were fixed between runs, broken down by severity. Reflects prioritization accuracy.",
  "Vulnerability Delta Table (Run 1 and Run 2)":
    "A detailed comparison of open (Run 1) and new (Run 2) vulnerabilities across Critical, High, Medium, and Low severities.",
  "Vulnerability Distribution by OWASP Top 10":
    "Categorizes issues based on OWASP Top 10 risk types (e.g., Injection, Broken Auth). Helps understand common threat types in the app for both run1 & run 2",
};

function ComparisonMetrics({ comparisonMetrics }) {
  const run1 = comparisonMetrics.run1Metrics || {};
  const run2 = comparisonMetrics.run2Metrics || {};

  const run2Remediation = {
    title: "Remediation Effectiveness",
    tooltipKey: "Remediation Effectiveness",
    chart: (
      <GaugeChartMUI
        value={run2.remediationEffectiveness.toFixed(1)}
        max={100}
        color="#ea580c"
        size={180}
      />
    ),
    description: "Remediation Rate",
  };

  const run2RiskExposure = {
    title: "Risk Exposure Reduction",
    tooltipKey: "Risk Exposure Reduction",
    chart: (
      <GaugeChartMUI
        value={run2.riskExposureReduction.toFixed(1)}
        max={100}
        color="#ea580c"
        size={180}
      />
    ),
    description: "Risk Reduction Rate",
  };
  const run2AttackSurface = {
    title: "Attack Surface Reduction",
    tooltipKey: "Attack Surface Reduction",
    chart: (
      <GaugeChartMUI
        value={run2.attackSurfaceReduction.toFixed(1)}
        max={10}
        color="#ea580c"
        size={180}
      />
    ),
    description: "Surface Reduction Rate",
  };

  const run2AverageAgeofVulnerabilities = {
    title: "Average Age of Vulnerabilities",
    tooltipKey: "Age of Vulnerabilities Resolved",
    // chart: (
    //   <TrendChartMUI
    //     value={run2.averageAgeOfVulnerability}
    //     trend="up"
    //     color="#dc2626"
    //     size={90}
    //   />
    // ),
    value: `${run2.averageAgeOfVulnerability} Days`,
    description: "Exposure Time",
  };

  const run2AverageTimeRemediation = {
    title: "Average time to Remediation",
    tooltipKey: "Average Time to Remediation",
    // chart: (
    //   <TrendChartMUI
    //     value={run2.averageTimeToRemediation}
    //     trend="up"
    //     color="#dc2626"
    //     size={90}
    //   />
    // ),
    value: `${run2.averageTimeToRemediation} Days`,
    description: "Average Fix Time",
  };

  // Transformed data
  const run2NotFixedVulnData = transformNotFixedSeverityData(
    run2.notFixedSeverityDistribution
  );

  // Transformed data
  const run2ClosedVulnData = transformClosedVulnData(
    run2.closedVulnerabilitiesBySeverity
  );

  // Transformed data
  const run2NewVulnerabilitiesData = transformNewVulnerabilitiesData(
    run2.newVulnerabilitiesBySeverity
  );

  // Transform the data
  const run1OwaspData = transformRun1OwaspData(run1.owasp_metrics);
  const run2OwaspData = transformRun2OwaspData(
    run2.owaspNotFixedVulnerabilities,
    run1OwaspData
  );

  // Your data
  const run1Data = run2.notFixedSeverityDistribution;
  const run2New = run2.newVulnerabilitiesBySeverity;
  const run2NotFixed = run2.notFixedSeverityDistribution;

  // Your specified color codes
  const severityColors = {
    Critical: "#c00000",
    High: "#ff0000",
    Medium: "#ffc000",
    Low: "#39b549",
  };

  // Prepare data for stacked/grouped view
  const chartData = [
    {
      name: "Critical",
      "Run 1 Open": run1Data.Critical,
      "Run 2 New": run2New.Critical,
    },
    { name: "High", "Run 1 Open": run1Data.High, "Run 2 New": run2New.High },
    {
      name: "Medium",
      "Run 1 Open": run1Data.Medium,
      "Run 2 New": run2New.Medium,
    },
    { name: "Low", "Run 1 Open": run1Data.Low, "Run 2 New": run2New.Low },
  ];

  const totalRun1 = Object.values(run1Data).reduce((sum, val) => sum + val, 0);
  const totalRun2 = Object.values(run2New).reduce((sum, val) => sum + val, 0);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      {/* Main Comparison Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Security Posture"
            // icon={Security}
            tooltipKey="Overall Security Posture" // key for tooltip text
            run1Value={`${
              run1.securityPosture ? run1.securityPosture.toFixed(1) : 0
            }%`}
            run1Label="Security Score"
            run2Value={`${
              run2.securityPosture ? run2.securityPosture.toFixed(1) : 0
            }%`}
            run2Label="Security Score"
            improvement={`${
              (run2.securityPosture || 0) - (run1.securityPosture || 0)
            }%`}
            improvementLabel="reduction"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Business Impact"
            // icon={Assessment}
            tooltipKey="Business Impact Change"
            run1Value={`${
              run1.businessImpact ? run1.businessImpact.toFixed(1) : 0
            }/10`}
            run1Label="Business Risk Score"
            run2Value={`${
              run2.businessImpact ? run2.businessImpact.toFixed(1) : 0
            }/10`}
            run2Label="Business Risk Score"
            improvement={`${
              (run2.businessImpact.toFixed(1) || 0) -
              (run1.businessImpact.toFixed(1) || 0)
            }%`}
            improvementLabel="reduction"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Technical Impact"
            // icon={Speed}
            tooltipKey="Technical Impact Change"
            run1Value={`${
              run1.technicalImpact ? run1.technicalImpact.toFixed(1) : 0
            }/10`}
            run1Label="Technical Risk Score"
            run2Value={`${
              run2.technicalImpact ? run2.technicalImpact.toFixed(1) : 0
            }/10`}
            run2Label="Technical Risk Score"
            improvement={`${
              (run2.technicalImpact.toFixed(1) || 0) -
              (run1.technicalImpact.toFixed(1) || 0)
            }%`}
            improvementLabel="reduction"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Vulnerability Count"
            // icon={Warning}
            tooltipKey="Vulnerability Count Change"
            run1Value={run1.vulnerabilitiesCount}
            run1Label="Total Vulnerabilities"
            run2Value={run2.notFixedVulnerabilities}
            run2Label="Total Vulnerabilities"
            improvement={`${
              run2.notFixedVulnerabilities - run1.vulnerabilitiesCount
            }`}
            improvementLabel="reduction"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Return on Investment (ROI)"
            // icon={AttachMoney}
            tooltipKey="ROI Change"
            run1Value={`${run1.ROI}%`}
            run1Label="ROI Multiplier"
            run2Value={`${run2.roiPercentage}%`}
            run2Label="ROI Multiplier"
            improvement={`${(run2.roiPercentage.toFixed(1) - run1.ROI).toFixed(
              1
            )}%`}
            improvementLabel="decrease"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Risk Rating"
            // icon={Timeline}
            tooltipKey="Risk Rating Change"
            run1Value={`${run1.riskRating.toFixed(1)}/5.5`}
            run1Label="Numeric Risk Score"
            run2Value={`${run2.riskRating.toFixed(1)}/5.5`}
            run2Label="Numeric Risk Score"
            improvement={`${(run2.riskRating - run1.riskRating).toFixed(1)}%`}
            improvementLabel="reduction"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ComparisonCard
            title="Risk Rating Weighted Score"
            // icon={Timeline}
            tooltipKey="Risk Rating Weighted Score Change"
            run1Value={`${run1.riskRatingWeightedScore.toFixed(1)}/10`}
            run1Label="Weighted Risk Score"
            run2Value={`${run2.riskRatingWeightedScore.toFixed(1)}/10`}
            run2Label="Weighted Risk Score"
            improvement={`${(
              run2.riskRatingWeightedScore - run1.riskRatingWeightedScore
            ).toFixed(1)}%`}
            improvementLabel="reduction"
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, md: 6 } }}>
        {[
          run2Remediation,
          run2RiskExposure,
          run2AttackSurface,
          run2AverageAgeofVulnerabilities,
          run2AverageTimeRemediation,
        ].map((metric, i) => (
          <Grid key={i} item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                pt: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* Info Icon - Top Right Corner */}
              <Tooltip
                title={tooltipTexts[metric.tooltipKey]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    paddingRight: 4,
                    paddingLeft: 4,
                  }}
                >
                  {metric.title}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between", // Changed from center
                    width: "100%",
                  }}
                >
                  {/* Chart and Value Section */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: 1,
                      justifyContent: "center",
                    }}
                  >
                    <Box>{metric.chart}</Box>
                    <Typography sx={{ fontSize: "29px", fontWeight: 600 }}>
                      {(metric.title === "Average Age of Vulnerabilities" ||
                        metric.title === "Average time to Remediation") &&
                        metric.value}
                    </Typography>
                  </Box>

                  {/* Description Section - Fixed at bottom */}
                  <Box
                    sx={{
                      textAlign: "center",
                      minHeight: "60px", // Fixed minimum height for descriptions
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                        // lineHeight: 1.4,
                      }}
                    >
                      {metric.description}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Vulnerability Analysis */}
      <Box
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 400, mb: 2 }} gutterBottom>
          Vulnerability Analysis
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <DonutChart
              data={run2NotFixedVulnData}
              title="Open Vulnerabilities by Category"
              centerText={run2NotFixedVulnData
                .reduce((sum, item) => sum + item.value, 0)
                .toString()}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: "100%",
                border: "1px solid #5391F7",
                borderRadius: 2,
                position: "relative",
              }}
            >
              {/* Tooltip positioned absolutely in top-right corner */}
              <Tooltip
                title={tooltipTexts["Closed Vulnerabilities by Severity"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    zIndex: 1, // Ensure tooltip stays on top
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <CardContent>
                {/* Centered Title */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3, // More spacing below title
                    // pt: 1, // Small top padding to account for tooltip
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      // fontWeight: 400,
                      // Responsive font sizes
                      paddingRight: 2,
                      paddingLeft: 2,
                      fontSize: {
                        xs: "1rem",
                        sm: "1.1rem",
                        md: "1.25rem",
                      },
                    }}
                  >
                    Closed Vulnerabilities by Severity
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  {run2ClosedVulnData.map((item, index) => {
                    const maxValue = Math.max(
                      ...run2ClosedVulnData.map((d) => d.value)
                    );

                    return (
                      <Box key={index}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          mb={0.5}
                        >
                          <Typography variant="body2">{item.name}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {item.value}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={
                            maxValue > 0 ? (item.value / maxValue) * 100 : 0
                          }
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "grey.200",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: item.color,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>

                <Box
                  sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total Closed
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {run2ClosedVulnData.reduce(
                        (sum, item) => sum + item.value,
                        0
                      )}
                    </Typography>
                  </Stack>
                </Box>
              </CardContent>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: "100%",
                border: "1px solid #5391F7",
                borderRadius: 2,
                position: "relative",
              }}
            >
              <Tooltip
                title={tooltipTexts["New Vulnerabilities in Run 2"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ paddingRight: 2, paddingLeft: 2 }}
                >
                  New Vulnerabilities in Run 2
                </Typography>
                <Stack spacing={2}>
                  {run2NewVulnerabilitiesData.map((item, index) => (
                    <Box key={index}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${item.color}40`,
                          bgcolor: `${item.color}10`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: item.color,
                            }}
                          />
                          <Typography variant="body2" fontWeight="medium">
                            {item.severity}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ color: item.color }}
                        >
                          {item.count}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                  <Divider />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {run2NewVulnerabilitiesData.reduce(
                        (sum, item) => sum + item.count,
                        0
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Paper
        sx={{
          p: 3,
          border: "1px solid #5391F7",
          borderRadius: 2,
          position: "relative", // ← Add this line
          mb: 4,
        }}
      >
        <Box>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <WarningIcon sx={{ color: "#d32f2f", mr: 1 }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Vulnerability Analysis (R1 vs R2)
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Total: {totalRun1} open vulnerabilities from Run 1, {totalRun2}{" "}
              new vulnerabilities identified in Run 2
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              {Object.entries(severityColors).map(([severity, color]) => (
                <Box
                  key={severity}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: color,
                      mr: 1,
                      borderRadius: "2px",
                    }}
                  />
                  <Typography variant="body2">{severity}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ height: 300, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    domain={[0, "dataMax + 1"]}
                  />

                  <Bar dataKey="Run 1 Open" barSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`run1-${index}`}
                        fill={severityColors[entry.name]}
                      />
                    ))}
                  </Bar>

                  <Bar dataKey="Run 2 New" barSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`run2-${index}`}
                        fill={severityColors[entry.name]}
                        fillOpacity={0.7} // Slightly transparent to differentiate
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* Dynamic chips for each severity level with not fixed count > 0 */}
              {Object.entries(run2NotFixed)
                .filter(([severity, count]) => count > 0)
                .map(([severity, count]) => (
                  <Chip
                    key={severity}
                    label={`${count} ${severity.toLowerCase()} not fixed`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: severityColors[severity] || "#666",
                      color: severityColors[severity] || "#666",
                      fontWeight: 500,
                    }}
                  />
                ))}

              {/* Open vulnerabilities chip - always show */}
              <Chip
                label={`${totalRun1} open vulnerabilities`}
                size="small"
                variant={totalRun1 > 0 ? "filled" : "outlined"}
                sx={{
                  backgroundColor: totalRun1 > 0 ? "#2196f3" : "transparent",
                  borderColor: totalRun1 === 0 ? "#4caf50" : "#2196f3",
                  color: totalRun1 > 0 ? "white" : "#4caf50",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      totalRun1 > 0 ? "#1976d2" : "rgba(76, 175, 80, 0.1)",
                  },
                }}
              />

              {/* New vulnerabilities chip - always show */}
              <Chip
                label={`${totalRun2} new vulnerabilities`}
                size="small"
                variant={totalRun2 > 0 ? "filled" : "outlined"}
                sx={{
                  backgroundColor: totalRun2 > 0 ? "#ff9800" : "transparent",
                  borderColor: totalRun2 === 0 ? "#4caf50" : "#ff9800",
                  color: totalRun2 > 0 ? "white" : "#4caf50",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      totalRun2 > 0 ? "#f57c00" : "rgba(76, 175, 80, 0.1)",
                  },
                }}
              />

              {/* Special message when both are 0 */}
              {totalRun2 === 0 && totalRun1 === 0 && (
                <Chip
                  label="Perfect security posture!"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
          </CardContent>
        </Box>
      </Paper>

      {/* OWASP Top 10 Comparison */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid #5391F7",
          borderRadius: 2,
          position: "relative", // ← Add this line
        }}
      >
        <Typography sx={{ fontSize: "23px", fontWeight: 400 }} gutterBottom>
          Vulnerability Distribution by OWASP Top 10
        </Typography>

        <Tooltip
          title={tooltipTexts["Vulnerability Distribution by OWASP Top 10"]}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>

        <OwaspComparisonChart
          run1Data={run1OwaspData}
          run2Data={run2OwaspData}
        />
      </Paper>
    </Box>
  );
}

export default ComparisonMetrics;
